import React from 'react';

import { Box, IconButton, TableCell, TableCellProps } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { ArrowUpwardOutlined } from '@mui/icons-material';

import { FlexTableSort, PinColumn, PinRow } from './FlexTable.component';

export type FlexTableCellProps = Omit<TableCellProps, 'onClick' | 'id' | 'height'> & {
  ['data-pincolumn']?: PinColumn;
  ['data-pinrow']?: PinRow;
  height: number;
  testId?: string;
};

export type FlexTableHeadCellProps = FlexTableCellProps & {
  children: React.ReactNode;
  columnSort?: FlexTableSort['type'] | null;
  sortable?: boolean;
  id: string;
  onSortClick: (colId: string) => void;
};

export const FlexTableHeadCell: React.FC<FlexTableHeadCellProps> = ({ children, columnSort, sortable, onSortClick, ...props }) => {
  return (
    <FlexTableCellBase {...props} onClick={sortable ? () => onSortClick(props.id) : undefined}>
      <Box
        display="flex"
        gap="0.25rem"
        justifyContent={props.align === 'right' ? 'end' : 'start'}
        flexDirection={props.align === 'right' ? 'row-reverse' : 'row'}
      >
        {children}
        {sortable && (
          <IconButton
            size="small"
            className={columnSort ? undefined : 'sort_icon-unsorted'}
          >
            <ArrowUpwardOutlined fontSize="small" className={columnSort === 'desc' ? 'rotate180' : undefined} />
          </IconButton>
        )}
      </Box>
    </FlexTableCellBase>
  );
};

export const FlexTableCell: React.VFC<FlexTableCellProps> = ({ children, testId = 'flex-table-cell', ...props }) => {
  return (
    <FlexTableCellBase {...props} data-testid={testId}>
      <Box display="flex" justifyContent={props.align === 'right' ? 'end' : 'start'}>
        {children}
      </Box>
    </FlexTableCellBase>
  );
};

export const FlexTableCellBase = withStyles(TableCell, (theme, { height }) => ({
  root: {
    height: `${height}px`,
    paddingTop: 0,
    paddingBottom: 0,
    background: theme.palette.common.white,
    whiteSpace: 'nowrap',
    '& > *': {
      overflow: 'hidden',
    },
    '&[data-pincolumn=start]': {
      position: 'sticky',
      left: 0,
      zIndex: 999,
      '&::after': {
        content: "''",
        position: 'absolute',
        height: '100%',
        left: '100%',
        top: 0,
        bottom: 0,
        width: '5px',
        boxShadow: 'inset 5px 0 5px -5px rgba(0,0,0,0.3)',
      },
    },
    '&[data-pincolumn=end]': {
      position: 'sticky',
      right: 0,
      zIndex: 999,
      '&::after': {
        content: "''",
        position: 'absolute',
        height: '100%',
        right: '100%',
        top: 0,
        bottom: 0,
        width: '5px',
        boxShadow: 'inset -5px 0 5px -5px rgba(0,0,0,0.3)',
      },
    },
    '&[data-pinrow=bottom]': {
      borderTop: `1px solid ${theme.palette.grey[300]}`
    }
  },
  head: {
    height: `${height}px`,
    paddingLeft: theme.spacing(2),
    '&[data-pincolumn=end]': {
      zIndex: 1000,
      '&::after': {
        background: 'white'
      }
    },
    '&[data-pincolumn=start]': {
      zIndex: 1000,
      '&::after': {
        background: 'white'
      }
    },
    backgroundColor: theme.palette.common.white,
    '& .sort_icon-unsorted': {
      visibility: 'hidden' as const
    },
    '&:hover': {
      '& .sort_icon-unsorted': {
        visibility: 'visible' as const,
        color: theme.palette.text.disabled
      }
    },
    '& .rotate180': {
      transform: 'rotate(180deg)',
    },
    left: 'auto',
    right: 'auto'
  }
}));
